/**
 * Secure logging utility for production safety
 * Hides debug messages in production environment
 */

// Determine if we're in production environment
const isProduction = process.env.NODE_ENV === 'production' || 
                     process.env.REACT_APP_ENV === 'production' ||
                     window.location.hostname !== 'localhost';

// Create secure logger that only shows messages in development
const logger = {
  log: (...args) => {
    if (!isProduction) {
      console.log(...args);
    }
  },
  
  info: (...args) => {
    if (!isProduction) {
      console.info(...args);
    }
  },
  
  warn: (...args) => {
    if (!isProduction) {
      console.warn(...args);
    }
  },
  
  error: (...args) => {
    // Only show critical errors in production, but still log them for debugging
    if (!isProduction) {
      console.error(...args);
    } else {
      // In production, only log to external error reporting service
      // You could integrate with services like Sentry, LogRocket, etc.
      // For now, we'll silently fail to hide sensitive information
    }
  },
  
  debug: (...args) => {
    // Debug messages are never shown in production
    if (!isProduction) {
      console.log('🔍 DEBUG:', ...args);
    }
  },
  
  // Analytics and payment logging - completely hidden in production
  payment: (...args) => {
    if (!isProduction) {
      console.log('💳 PAYMENT:', ...args);
    }
  },
  
  analytics: (...args) => {
    if (!isProduction) {
      console.log('📊 ANALYTICS:', ...args);
    }
  },
  
  // Security-sensitive logging
  security: (...args) => {
    if (!isProduction) {
      console.log('🔒 SECURITY:', ...args);
    }
  }
};

// Override global console methods in production to prevent accidental logging
if (isProduction) {
  // Store original methods for internal use if needed
  window._originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug
  };
  
  // Replace global console methods with no-ops in production
  console.log = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.debug = () => {};
  
  // Only allow critical error logging in production (but sanitize it)
  console.error = (...args) => {
    // Only log generic error messages, no sensitive data
    if (args.length > 0 && typeof args[0] === 'string') {
      const sanitizedMessage = args[0].replace(/token|key|secret|password|email/gi, '[REDACTED]');
      window._originalConsole.error('Application Error:', sanitizedMessage);
    }
  };
}

export default logger;